class VariantDropdown extends HTMLElement {
  constructor() {
    super();
    this.selects = this.querySelectorAll('select');
    this.customDropdowns = [];
    
    this.init();
  }

  init() {
    // Convert each native select to a custom dropdown
    this.selects.forEach((select, index) => {
      this.createCustomDropdown(select, index);
    });
  }

  createCustomDropdown(select, index) {
    // Hide the original select
    select.style.display = 'none';
    
    // Get the fieldset wrapper
    const fieldset = select.closest('fieldset');
    if (!fieldset) return;

    const label = fieldset.querySelector('label');
    const selectWrapper = fieldset.querySelector('.select');
    
    // Create custom dropdown structure
    const dropdown = document.createElement('div');
    dropdown.className = 'variant-dropdown__custom';
    dropdown.dataset.variantDropdown = index;
    
    // Create dropdown button
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'variant-dropdown__button';
    button.dataset.variantButton = index;
    button.setAttribute('aria-expanded', 'false');
    
    const selectedOption = select.options[select.selectedIndex];
    button.innerHTML = `
      <div class="variant-dropdown__button-content">
        <span class="variant-dropdown__button-text" data-variant-text="${index}">${selectedOption.text}</span>
      </div>
      <svg class="variant-dropdown__button-arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="4 6 8 10 12 6"></polyline>
      </svg>
    `;
    
    // Create dropdown list
    const list = document.createElement('div');
    list.className = 'variant-dropdown__list';
    list.dataset.variantList = index;
    
    // Add options to list
    Array.from(select.options).forEach((option, optionIndex) => {
      const optionDiv = document.createElement('div');
      optionDiv.className = 'variant-dropdown__option';
      optionDiv.dataset.variantOption = option.value;
      optionDiv.dataset.optionIndex = optionIndex;
      
      if (option.selected) {
        optionDiv.classList.add('variant-dropdown__option--selected');
      }
      
      // Check if option is disabled/unavailable
      const isUnavailable = option.textContent.includes(window.variantStrings?.unavailable_with_option || 'Unavailable');
      if (option.disabled || isUnavailable) {
        optionDiv.classList.add('variant-dropdown__option--disabled');
      }
      
      const content = document.createElement('div');
      content.className = 'variant-dropdown__option-content';
      
      const name = document.createElement('span');
      name.className = 'variant-dropdown__option-name';
      name.textContent = option.textContent;
      
      content.appendChild(name);
      optionDiv.appendChild(content);
      list.appendChild(optionDiv);
    });
    
    dropdown.appendChild(button);
    dropdown.appendChild(list);
    
    // Replace the select wrapper with our custom dropdown
    if (selectWrapper) {
      selectWrapper.innerHTML = '';
      selectWrapper.appendChild(dropdown);
      selectWrapper.appendChild(select); // Keep original select for form submission
    }
    
    // Setup event listeners
    this.setupDropdownListeners(button, list, select, index);
    
    // Store reference
    this.customDropdowns.push({ dropdown, button, list, select });
  }

  setupDropdownListeners(button, list, select, index) {
    let isOpen = false;
    
    // Toggle dropdown
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Close other dropdowns
      this.customDropdowns.forEach((dd, i) => {
        if (i !== index && dd.dropdown.classList.contains('variant-dropdown__custom--open')) {
          this.closeDropdown(dd.button, dd.list);
        }
      });
      
      if (isOpen) {
        this.closeDropdown(button, list);
        isOpen = false;
      } else {
        this.openDropdown(button, list);
        isOpen = true;
      }
    });
    
    // Handle option clicks
    list.querySelectorAll('.variant-dropdown__option').forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (option.classList.contains('variant-dropdown__option--disabled')) {
          return;
        }
        
        const value = option.dataset.variantOption;
        const optionIndex = option.dataset.optionIndex;
        
        // Update native select
        select.selectedIndex = optionIndex;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Update button text
        const buttonText = button.querySelector(`[data-variant-text="${index}"]`);
        if (buttonText) {
          buttonText.textContent = option.textContent;
        }
        
        // Update selected state
        list.querySelectorAll('.variant-dropdown__option').forEach(opt => {
          opt.classList.remove('variant-dropdown__option--selected');
        });
        option.classList.add('variant-dropdown__option--selected');
        
        // Close dropdown
        this.closeDropdown(button, list);
        isOpen = false;
      });
    });
    
    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!button.contains(e.target) && !list.contains(e.target)) {
        this.closeDropdown(button, list);
        isOpen = false;
      }
    });
    
    // Keyboard navigation
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        button.click();
      } else if (e.key === 'Escape') {
        this.closeDropdown(button, list);
        isOpen = false;
      }
    });
  }

  openDropdown(button, list) {
    const dropdown = button.closest('.variant-dropdown__custom');
    dropdown.classList.add('variant-dropdown__custom--open');
    button.setAttribute('aria-expanded', 'true');
  }

  closeDropdown(button, list) {
    const dropdown = button.closest('.variant-dropdown__custom');
    dropdown.classList.remove('variant-dropdown__custom--open');
    button.setAttribute('aria-expanded', 'false');
  }

  // Method to update options when variant availability changes
  updateOptionAvailability() {
    this.customDropdowns.forEach(({ list, select }) => {
      const options = list.querySelectorAll('.variant-dropdown__option');
      
      options.forEach((optionDiv, index) => {
        const selectOption = select.options[index];
        const isUnavailable = selectOption.textContent.includes(window.variantStrings?.unavailable_with_option || 'Unavailable');
        
        if (selectOption.disabled || isUnavailable) {
          optionDiv.classList.add('variant-dropdown__option--disabled');
        } else {
          optionDiv.classList.remove('variant-dropdown__option--disabled');
        }
        
        // Update text to match select option (in case it changed)
        const nameSpan = optionDiv.querySelector('.variant-dropdown__option-name');
        if (nameSpan) {
          nameSpan.textContent = selectOption.textContent;
        }
      });
    });
  }
}

// Extend VariantSelects to work with our custom dropdown
class VariantDropdownSelects extends VariantSelects {
  constructor() {
    super();
    
    // Initialize custom dropdown after parent constructor
    this.initCustomDropdown();
  }

  initCustomDropdown() {
    // Wait for DOM to be ready
    setTimeout(() => {
      this.customDropdown = new VariantDropdown();
      this.appendChild(this.customDropdown);
    }, 0);
  }

  // Override updateVariantStatuses to also update our custom dropdown
  updateVariantStatuses() {
    super.updateVariantStatuses();
    
    // Update custom dropdown options
    if (this.customDropdown) {
      this.customDropdown.updateOptionAvailability();
    }
  }
}

// Define custom elements
if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}

// Note: We don't redefine variant-selects here to avoid conflicts
// Instead, we'll manually initialize variant-dropdown in the template

